import {useCallback, useMemo, useSyncExternalStore} from 'react';
import {languageFavorites} from '../services/languageFavoritesService';
import {useUser} from '../contexts/UserContext';

// React binding for the favorite-languages store. Any picker that calls this
// hook re-renders the moment a star is toggled anywhere in the session, and the
// toggle is mirrored to Firestore for the signed-in provider so it follows them
// across devices.
export default function useLanguageFavorites() {
  // `useUser` returns undefined outside a UserProvider (e.g. some unit tests
  // mount pickers in isolation). Favorites still work locally in that case; the
  // background Firestore sync simply no-ops without a user id.
  const userId = useUser()?.userData?.userId;

  const favorites = useSyncExternalStore(
    languageFavorites.subscribe.bind(languageFavorites),
    languageFavorites.getSnapshot,
    languageFavorites.getSnapshot,
  );

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  const isFavorite = useCallback(
    (language: unknown) => favoriteSet.has(String(language ?? '').trim()),
    [favoriteSet],
  );

  const toggleFavorite = useCallback(
    (language: unknown) => {
      const next = languageFavorites.toggleFavorite(language);
      // Persist in the background; a failed write just means this toggle stays
      // local to the device, which the next successful sync reconciles.
      languageFavorites.syncToFirebase(userId).catch(() => {});
      return next;
    },
    [userId],
  );

  return {favorites, favoriteSet, isFavorite, toggleFavorite};
}
